import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Card for a single liked item (Storybook or Video).
 * Click: Storybook → /audiobooks/:id, Video → /stories/:id (story detail page)
 */
const LikedContentCard = ({
  id,
  type,
  title,
  description,
  thumbnail,
  views,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!id) return;
    if (type === "storybook") {
      navigate(`/audiobooks/${id}`);
    } else {
      navigate(`/stories/${id}`);
    }
  };

  const badgeLabel = type === "storybook" ? "Storybook" : "Video";
  const displayTitle = title || (type === "storybook" ? "Untitled Storybook" : "Untitled Video");

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group text-left flex flex-col w-full min-w-0 rounded-2xl overflow-hidden transition-all duration-200 md:min-w-[220px] md:max-w-[220px] md:flex-shrink-0 md:rounded-xl md:bg-white/5 md:backdrop-blur md:border md:border-white/10 md:hover:border-[#6B5EF1]/50 md:hover:shadow-lg md:hover:shadow-[#4B3BE5]/10"
    >
      <div className={`relative ${type === "storybook" ? "aspect-[2/3]" : "aspect-[3/4]"} bg-gradient-to-br from-[#4B3BE5]/10 to-[#6B5EF1]/10 overflow-hidden flex-shrink-0`}>
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.classList.remove("hidden");
            }}
          />
        ) : null}
        <div
          className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#4B3BE5]/20 to-[#6B5EF1]/20 ${thumbnail ? "hidden" : ""}`}
          aria-hidden
        >
          <span className="text-4xl opacity-80">
            {type === "storybook" ? "📖" : "🎬"}
          </span>
        </div>
        {/* Gradient overlay: stronger at bottom on mobile for title readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent md:bg-black/50" aria-hidden />
        <div className="absolute bottom-0 left-0 right-0 p-3 flex flex-col justify-end min-h-[50%] md:min-h-[60%]">
          <span className="self-start px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white border border-white/30 mb-1.5 md:mb-2">
            {badgeLabel}
          </span>
          <h3 className="text-sm font-bold text-white line-clamp-2 group-hover:text-[#6B5EF1] transition-colors md:mb-1">
            {displayTitle}
          </h3>
          <p className="hidden md:block text-xs text-gray-400">
            <span aria-hidden>👁</span> {views ?? 0} views
          </p>
        </div>
      </div>
    </button>
  );
};

export default LikedContentCard;
